import { RootState } from '../../redux/reducers';
import { store } from '../../redux/store';
import {
  CreateEventModel,
  Event,
  EventParticipationAnswer,
  ExternalEventInvite,
  RecurrenceEditType,
  UpdateEventModel,
} from '../../types/event';
import httpService from '../httpService';
import {
  createEvent as createEventRedux,
  deleteEvent as deleteEventRedux,
  setAttendance as setAttendanceRedux,
  setEvents,
  updateEvent as updateEventRedux,
} from '../../redux/slices/eventSlice';
import { addDateToEvent } from '../../redux/transformers';

async function createEvent(
  event: CreateEventModel,
  participantsIds: number[] = [],
  inviteAll = true,
  inviteActive = false,
  externalInvites: { externalMemberId: number }[] = [],
): Promise<Event> {
  const { priceOfTicket, ...eventBody } = event;
  const communityId = store.getState().application.selectedCommunity;
  const { data } = await httpService.post<Event>(`/networks/communities/${communityId}/events`, {
    ...eventBody,
    participantsIds,
    inviteAll,
    inviteActive,
    externalInvites,
    canUnselectYourself: true,
    priceOfTicket,
  });
  const transformed = addDateToEvent(data);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(createEventRedux({ communityId: selectedCommunity!, event: transformed }));
  return transformed;
}

async function generateEventInviteLink(eventId: number): Promise<string> {
  const { data } = await httpService.post(`/events/${eventId}/invite-link`);
  return data.invite;
}

function getUpcomingEvents(): Promise<Event[]> {
  const { selectedCommunity } = (store.getState() as RootState).application;
  return httpService.get<Event[]>(`/networks/communities/${selectedCommunity}/events/upcoming`).then(({ data }) => {
    const events = data.map(addDateToEvent);
    store.dispatch(setEvents({ communityId: selectedCommunity!, events }));
    return events;
  });
}

async function getEventAnalytics(from: Date, to: Date): Promise<Event[]> {
  const communityId = store.getState().application.selectedCommunity;
  const { data } = await httpService.get<Event[]>(`/networks/communities/${communityId}/events/analytics`, {
    params: { from, to },
  });
  const events = data.map(addDateToEvent);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(setEvents({ communityId: selectedCommunity!, events }));
  return events;
}

async function prependPastEvents(): Promise<Event[]> {
  const { selectedCommunity } = (store.getState() as RootState).application;
  return httpService.get<Event[]>(`/networks/communities/${selectedCommunity}/events/past`).then(({ data }) => {
    const pastEvents = data.map(addDateToEvent);
    const upcomingEvents = (store.getState() as RootState).events.eventMap[selectedCommunity!];
    const merged = [...pastEvents, ...upcomingEvents];
    store.dispatch(setEvents({ communityId: selectedCommunity!, events: merged }));
    return merged;
  });
}

async function deleteEvent(
  eventId: number,
  deleteRecurrences: RecurrenceEditType = RecurrenceEditType.SINGLE,
): Promise<void> {
  await httpService.delete(`/events/${eventId}?recurrence=${deleteRecurrences}`);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(deleteEventRedux({ communityId: selectedCommunity!, eventId }));
}

async function updateEvent(
  eventId: number,
  event: UpdateEventModel,
  updateRecurrences?: RecurrenceEditType,
): Promise<Event> {
  const { data } = await httpService.put<Event>(`/events/${eventId}`, {
    ...event,
    updateRecurrences,
  });
  const transformed = addDateToEvent(data);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(updateEventRedux({ communityId: selectedCommunity!, event: transformed }));
  return transformed;
}

async function setAttendance(
  eventId: number,
  participantId: number,
  answer: EventParticipationAnswer,
): Promise<void> {
  await httpService.put(`/events/${eventId}/participants/${participantId}/attendance`, { answer });
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(setAttendanceRedux({ communityId: selectedCommunity!, participantId, answer }));
}

async function remindUserForEvent(eventId: number, participantId: number): Promise<void> {
  await httpService.post(`/events/${eventId}/remind/${participantId}`);
}

async function remindExternalIndividualForEventInvite(eventId: number, externalInviteId: number): Promise<void> {
  await httpService.post(`/events/${eventId}/remind-external-invite/${externalInviteId}`);
}

function deleteParticipant(eventId: number, participantId: number): Promise<Event> {
  const {
    application: { selectedCommunity },
    events: { eventMap },
  } = store.getState() as RootState;

  const participantIds = eventMap[selectedCommunity!]
    ?.find((e) => e.id === eventId)
    ?.participants.map((p) => p.id)
    .filter((id) => id !== participantId);

  const externalInvites = eventMap[selectedCommunity!]
    ?.find((e) => e.id === eventId)
    ?.externalInvites.map((invite: ExternalEventInvite) => ({ externalMemberId: invite.externalMemberId }));

  if (!participantIds) throw new Error('Event Not Found');
  return updateParticipants(eventId, participantIds, externalInvites);
}

function deleteExternalMemberInvite(eventId: number, externalMemberId: number): Promise<Event> {
  const {
    application: { selectedCommunity },
    events: { eventMap },
  } = store.getState() as RootState;

  const participantIds = eventMap[selectedCommunity!]
    ?.find((e) => e.id === eventId)
    ?.participants.map((p) => p.id);

  const externalInvites = eventMap[selectedCommunity!]
    ?.find((e) => e.id === eventId)
    ?.externalInvites.map((invite: ExternalEventInvite) => ({ externalMemberId: invite.externalMemberId }))
    .filter((invite) => invite.externalMemberId !== externalMemberId);

  if (!participantIds || !externalInvites) throw new Error('Event Not Found');
  return updateParticipants(eventId, participantIds, externalInvites);
}

async function updateParticipants(
  eventId: number,
  participantsIds: number[],
  externalInvites: { externalMemberId: number }[] = [],
  inviteAll?: boolean,
  inviteActive?: boolean,
): Promise<Event> {
  const body: {
    participantsIds: typeof participantsIds;
    externalInvites: typeof externalInvites;
    inviteAll?: boolean;
    inviteActive?: boolean;
  } = {
    participantsIds,
    externalInvites,
  };

  if (inviteAll !== undefined) body.inviteAll = inviteAll;
  if (inviteActive !== undefined) body.inviteActive = inviteActive;

  await httpService.post(`/events/${eventId}/participants`, body);
  return fetchEvent(eventId);
}

async function fetchEvent(eventId: number): Promise<Event> {
  const { data } = await httpService.get<Event>(`/events/${eventId}`);
  const transformed = addDateToEvent(data);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(updateEventRedux({ communityId: selectedCommunity!, event: transformed }));
  return transformed;
}

async function pickDateOptions(eventId: number, dateOptionIds: number[]): Promise<Event[]> {
  const { data } = await httpService.post(`/events/${eventId}/date-options/convert-bulk`, {
    dateOptionIds,
  });
  await getUpcomingEvents();
  return data;
}

async function uploadCover(eventId: number, file: File): Promise<Event> {
  const formData = new FormData();
  formData.append('picture', file);
  const { data } = await httpService.put<Event>(`/events/${eventId}/cover`, formData);
  const transformed = addDateToEvent(data);
  const { selectedCommunity } = (store.getState() as RootState).application;
  store.dispatch(updateEventRedux({ communityId: selectedCommunity!, event: transformed }));
  return transformed;
}

export default {
  createEvent,
  getUpcomingEvents,
  prependPastEvents,
  getEventAnalytics,
  deleteEvent,
  updateEvent,
  setAttendance,
  remindUserForEvent,
  remindExternalIndividualForEventInvite,
  updateParticipants,
  fetchEvent,
  deleteParticipant,
  deleteExternalMemberInvite,
  pickDateOptions,
  generateEventInviteLink,
  uploadCover,
};
